import React, { useState, useRef, useEffect } from 'react';
import { FiFolder, FiMoreHorizontal } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import { useNavigate } from 'react-router-dom';

const albumCategories = [
  { category: "Wedding Memories", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/marriage.png" },
  { category: "Birthday Celebrations", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/birthday.png" },
  { category: "Baby Shower & Newborn", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/nb.png" },
  { category: "Graduation Moments", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/graduation.png" },
  { category: "Anniversary Specials", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/anniversary.png" },
  { category: "Family Gatherings & Reunions", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/familyGatherings.png" },
  { category: "Holiday & Festival Fun", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/fastival.png" },
  { category: "Travel & Adventures", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/travel%26adventures.png" },
  { category: "Corporate Events & Conferences", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/corporate.png" },
  { category: "Farewell & Retirement Parties", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/farewell.png" },
  { category: "default", url: "https://storage.googleapis.com/heartfelt-6a946.firebasestorage.app/Album-defalut/default-album.jpg" }
];

const getThumbnailFromCategory = (category: string) => {
  const found = albumCategories.find(c => c.category === category);
  return found ? found.url : albumCategories.find(c => c.category === 'default')?.url;
};

interface AlbumCardProps {
  albumId: string;
  title: string;
  category?: string;
  tags?: string[];
  createdBy: string;
}

const AlbumCard: React.FC<AlbumCardProps> = ({ albumId, title, category, tags, createdBy }) => {
  const thumbnail = getThumbnailFromCategory(category || '');
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const authState = useSelector((state: RootState) => state.auth);

  const user = authState.user;

  const handleOpenAlbum = () => {
    navigate(`/dashboard/${albumId}/${encodeURIComponent(title)}/${encodeURIComponent(createdBy)}`);
  };

  const handleEdit = () => {
    const confirmed = window.confirm('Do you really want to edit this album?');
    if (confirmed) {
      navigate(`/edit-album/${albumId}`);
    }
  };

  const handleDelete = () => {
    const confirmed = window.confirm('Do you really want to delete this album?');
    if (confirmed) {
      // Call your delete function or API here
      console.log('Delete album:', albumId);
    }
  };

  const handleShare = async () => {
    const shareUrl = `https://theheartfelt.in/share-album/${albumId}/${user?.id}`;
    const message = `${user?.name} has shared the "${title}" album with you:\n${shareUrl}`;

    try {
      await navigator.clipboard.writeText(message);
  
      // Optional: open a WhatsApp share prompt
      const whatsappLink = `https://wa.me/?text=${encodeURIComponent(`Check out this album: ${message}`)}`;
      const shareOptions = window.confirm('Do you want to share this via WhatsApp?');
      if (shareOptions) {
        window.open(whatsappLink, '_blank');
      }
  
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy the link to clipboard.');
    }
  };
  

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative bg-white shadow-lg rounded-xl overflow-hidden hover:shadow-xl transition duration-300 group">
      {/* Three dots menu */}
      <div className="absolute top-2 right-2 z-20" ref={menuRef}>
        <button
          className="text-black hover:text-black p-1 rounded-full"
          onClick={() => setMenuOpen(prev => !prev)}
        >
          <FiMoreHorizontal size={18} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-black hover:bg-black hover:text-white"
              onClick={handleEdit}
            >
              Edit
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-black hover:bg-black hover:text-white"
              onClick={handleShare}
            >
              Share
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-black hover:bg-black hover:text-white"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Thumbnail image */}
      <img
        src={thumbnail}
        alt={title}
        className="w-full h-40 object-cover transition duration-300 group-hover:blur-sm"
      />

      {/* Open Album button */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
        <button
          onClick={handleOpenAlbum}
          className="bg-black backdrop-blur-md px-4 py-2 rounded-full shadow-md flex items-center gap-2 text-sm font-medium hover:bg-black"
        >
          <FiFolder size={20} />
          Open
        </button>
      </div>

      {/* Title and tags */}
      <div className="p-4">
        <h3 className="font-semibold truncate text-black">{title}</h3>
        <p className="text-xs text-black truncate">{tags?.join(', ') || 'No tags'}</p>
      </div>
    </div>
  );
};

export default AlbumCard;
